import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { flowFor } from "@/data/products";
import api from "@/lib/api";
import { CheckCircle2, Phone, ChevronLeft } from "lucide-react";
import { useContent } from "@/components/PublicLayout";

const FLOWS = {
  auto: [
    { title: "Contact Information", fields: [["first_name","First Name"],["last_name","Last Name"],["phone","Phone"],["email","Email"],["zip_code","ZIP Code"]] },
    { title: "Vehicle Information", fields: [["vehicle_year","Vehicle Year"],["vehicle_make","Make"],["vehicle_model","Model"],["num_vehicles","Number of Vehicles"]] },
    { title: "Driver Information", fields: [["dob","Date of Birth","date"],["license_status","License Status",["Valid","Suspended","New Driver"]],["accidents","Accidents in last 3 years?",["None","1","2+"]]] },
    { title: "Current Insurance", fields: [["current_carrier","Current Insurance Company"],["current_premium","Current Monthly Premium"],["expiration","Policy Expiration","date"]] },
    { title: "Coverage Preferences", fields: [["coverage_level","Coverage Level",["State Minimum","Standard","Full Coverage"]],["notes","Anything else we should know?","textarea"]] },
  ],
  home: [
    { title: "Contact Information", fields: [["first_name","First Name"],["last_name","Last Name"],["phone","Phone"],["email","Email"],["zip_code","ZIP Code"]] },
    { title: "Property Address", fields: [["address","Street Address"],["city","City"],["state","State"]] },
    { title: "Property Information", fields: [["year_built","Year Built"],["sq_ft","Square Footage"],["home_type","Home Type",["Single Family","Townhouse","Condo","Renter"]]] },
    { title: "Current Insurance", fields: [["current_carrier","Current Insurance Company"],["current_premium","Current Premium"],["expiration","Policy Expiration","date"]] },
    { title: "Coverage Preferences", fields: [["bundle_auto","Bundle with Auto?",["Yes","No","Maybe"]],["notes","Anything else we should know?","textarea"]] },
  ],
  commercial: [
    { title: "Contact Information", fields: [["first_name","Contact Name"],["phone","Phone"],["email","Email"],["business_name","Business Name"]] },
    { title: "Business Information", fields: [["address","Business Address"],["business_type","Type of Business"],["years","Years in Business"],["employees","Number of Employees"]] },
    { title: "Current Coverage", fields: [["current_carrier","Current Insurance Company"],["expiration","Current Policy Expiration","date"]] },
    { title: "Coverage Requested", fields: [["coverage_needed","Coverage Needed"],["notes","Additional Information","textarea"]] },
    { title: "Documents", fields: [["documents","Upload current policy / documents (optional)","file"]] },
  ],
  life: [
    { title: "Contact Information", fields: [["first_name","First Name"],["last_name","Last Name"],["phone","Phone"],["email","Email"],["zip_code","ZIP Code"]] },
    { title: "Basic Eligibility Information", fields: [["dob","Date of Birth","date"],["gender","Gender",["Male","Female","Prefer not to say"]],["tobacco","Tobacco Use?",["No","Yes"]]] },
    { title: "Coverage Interest", fields: [["coverage_amount","Coverage Amount Interested In",["$25k","$50k","$100k","$250k","$500k+","Not sure"]],["life_type","Type",["Term","Whole","Final Expense","Not sure"]]] },
    { title: "Contact Preferences", fields: [["preferred_contact","Preferred Contact Method",["Phone","Email","Text"]],["notes","Anything else we should know?","textarea"]] },
  ],
};

export default function QuoteFunnel() {
  const loc = useLocation();
  const nav = useNavigate();
  const content = useContent();
  const preset = loc.state || {};
  const flow = useMemo(() => flowFor(preset.types || []), [preset.types]);
  const steps = FLOWS[flow];
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ ...(preset.lead || {}) });
  const [leadId, setLeadId] = useState(null);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploaded, setUploaded] = useState([]);
  const phone = content?.phone || "(570) 661-3771";

  const set = (name) => (e) => setData({ ...data, [name]: e.target.value });

  const persist = async (completion, extra = {}) => {
    const body = {
      first_name: data.first_name || "", last_name: data.last_name || "",
      phone: data.phone || "", email: data.email || "", zip_code: data.zip_code || "",
      insurance_types: preset.types || [], source: preset.source || (preset.utm?.utm_source ? "Website" : "Website"),
      utm_source: preset.utm?.utm_source, utm_medium: preset.utm?.utm_medium, utm_campaign: preset.utm?.utm_campaign,
      campaign_slug: preset.campaign_slug || null,
      business_name: data.business_name || null,
      answers: { ...data }, completion, ...extra,
    };
    if (!leadId) {
      const { data: res } = await api.post("/quote", body);
      setLeadId(res.lead_id);
      return res;
    } else {
      await api.patch(`/quote/${leadId}`, body);
      return { lead_id: leadId };
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    let id = leadId;
    try {
      if (!id) { const res = await persist(25); id = res.lead_id; setLeadId(id); }
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", "Current Policy");
      await api.post(`/quote/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded((u) => [...u, file.name]);
    } catch (err) { /* silent */ }
  };

  const next = async () => {
    setSaving(true);
    try {
      const completion = Math.round(((step + 1) / steps.length) * 100);
      if (step === 0 || leadId) await persist(Math.min(completion, 99));
      if (step === steps.length - 1) { await persist(100); setDone(true); }
      else setStep(step + 1);
    } finally { setSaving(false); }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-5 py-20 text-center animate-rise">
        <CheckCircle2 className="text-[#C5A059] mx-auto mb-6" size={64} />
        <h1 className="font-display text-4xl font-bold" data-testid="funnel-thankyou">THANK YOU!</h1>
        <p className="mt-3 text-lg">Your quote request has been received.</p>
        <p className="mt-2 text-slate-600">A Keystone Shield Insurance representative will review your information and contact you shortly.</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          {flow === "life" && content?.life_quote_link && (
            <a href={content.life_quote_link} target="_blank" rel="noopener noreferrer" className="bg-[#C5A059] text-[#0B1E36] font-bold px-6 h-12 rounded-sm flex items-center justify-center" data-testid="funnel-ethos">GET INSTANT LIFE QUOTE</a>
          )}
          <a href={`tel:${phone}`} className="bg-[#0B1E36] text-white font-bold px-6 h-12 rounded-sm flex items-center justify-center gap-2" data-testid="funnel-call"><Phone size={18} /> CALL US NOW</a>
          <Link to="/" className="border border-slate-300 px-6 h-12 rounded-sm flex items-center justify-center font-semibold">Back to Home</Link>
        </div>
      </div>
    );
  }

  const current = steps[step];
  const input = "w-full h-12 px-4 rounded-sm border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#C5A059]";

  return (
    <div className="max-w-2xl mx-auto px-5 py-10">
      <div className="mb-6">
        {step > 0 && <button onClick={() => setStep(step - 1)} className="text-sm text-slate-500 flex items-center gap-1 mb-3" data-testid="funnel-back"><ChevronLeft size={16} /> Back</button>}
        <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-bold" data-testid="funnel-progress">Step {step + 1} of {steps.length}</p>
        <div className="h-1.5 bg-slate-200 rounded-full mt-2 overflow-hidden">
          <div className="h-full bg-[#C5A059] transition-all duration-300" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
        </div>
        <h1 className="font-display text-3xl font-bold mt-5">{current.title}</h1>
      </div>
      <div className="space-y-4 bg-white border border-slate-200 rounded-sm p-6">
        {current.fields.map(([name, label, type]) => (
          <div key={name}>
            <label className="text-sm font-semibold block mb-1.5">{label}</label>
            {Array.isArray(type) ? (
              <select className={input} value={data[name] || ""} onChange={set(name)} data-testid={`funnel-${name}`}>
                <option value="">Select…</option>
                {type.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : type === "textarea" ? (
              <textarea className="w-full min-h-[90px] p-3 rounded-sm border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#C5A059]" value={data[name] || ""} onChange={set(name)} data-testid={`funnel-${name}`} />
            ) : type === "file" ? (
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx" className="w-full text-sm" onChange={handleFile} data-testid={`funnel-${name}`} />
            ) : (
              <input type={type || "text"} className={input} value={data[name] || ""} onChange={set(name)} data-testid={`funnel-${name}`} />
            )}
          </div>
        ))}
        {step === steps.length - 1 && flow !== "commercial" && (
          <div className="border-t border-slate-100 pt-4">
            <label className="text-sm font-semibold block mb-1.5">📎 Upload your current policy so we can match or beat it <span className="text-slate-400 font-normal">(optional)</span></label>
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx" className="w-full text-sm" onChange={handleFile} data-testid="funnel-upload-policy" />
          </div>
        )}
        {uploaded.length > 0 && (
          <div className="text-sm text-green-700 bg-green-50 rounded-sm px-3 py-2" data-testid="funnel-uploaded">✓ Uploaded: {uploaded.join(", ")}</div>
        )}
      </div>
      <button onClick={next} disabled={saving} className="mt-6 w-full bg-[#C5A059] text-[#0B1E36] font-bold text-base h-14 rounded-sm hover:bg-[#0B1E36] hover:text-white transition-colors disabled:opacity-60" data-testid="funnel-next">
        {saving ? "Saving…" : step === steps.length - 1 ? "SUBMIT MY QUOTE REQUEST" : "CONTINUE"}
      </button>
    </div>
  );
}
